import { db, now, MappingEntry, StructureEntry, SyncQueueItem } from './database';
import { triggerImmediateUpload } from '../sync/syncEngine';

/**
 * Numero intervento condiviso fra mappature e strutture.
 *
 * Le due entità vivono in tabelle diverse ma condividono la stessa sequenza:
 * sullo stesso piano lo stesso numero non può comparire due volte, né fra due
 * mappature né fra una mappatura e una struttura. La stanza non entra nella
 * sequenza: la numerazione è unica per piano.
 */

/** Voce della sequenza: una mappatura o una struttura, trattate allo stesso modo */
type InterventionEntry =
  | { kind: 'mapping'; entry: MappingEntry }
  | { kind: 'structure'; entry: StructureEntry };

function compareInterventionValues(a?: string, b?: string): number {
  const valueA = (a || '').trim();
  const valueB = (b || '').trim();
  const numA = Number(valueA);
  const numB = Number(valueB);
  const aIsNum = valueA !== '' && Number.isFinite(numA);
  const bIsNum = valueB !== '' && Number.isFinite(numB);

  if (aIsNum && bIsNum) return numA - numB;
  return valueA.localeCompare(valueB, 'it', { numeric: true, sensitivity: 'base' });
}

function buildFloorPlanLabel(entry: MappingEntry | StructureEntry, fallback: string): string[] {
  const parts: string[] = [];
  if (entry.floor) parts.push(`P${entry.floor}`);
  if (entry.room) parts.push(`S${entry.room}`);
  if (entry.intervention) parts.push(`Int${entry.intervention}`);
  return [parts.length > 0 ? parts.join('_') : fallback];
}

async function upsertPendingSyncItem(
  entityType: SyncQueueItem['entityType'],
  entityId: string,
  payload: unknown
): Promise<void> {
  const existingSyncItem = await db.syncQueue
    .where('entityType')
    .equals(entityType)
    .and((item) => item.entityId === entityId && item.synced === 0 && item.operation !== 'DELETE')
    .first();

  if (existingSyncItem) {
    await db.syncQueue.update(existingSyncItem.id, { payload, timestamp: now() });
    return;
  }

  await db.syncQueue.add({
    id: crypto.randomUUID(),
    operation: 'UPDATE',
    entityType,
    entityId,
    payload,
    timestamp: now(),
    retryCount: 0,
    synced: 0,
  });
}

/** Riallinea le etichette dei punti planimetria collegati alla voce rinumerata */
async function updateLinkedFloorPlanPoints(
  entryId: string,
  labelText: string[]
): Promise<void> {
  const linkedPoints = await db.floorPlanPoints
    .filter((point) => point.mappingEntryId === entryId || point.structureEntryId === entryId)
    .toArray();

  for (const point of linkedPoints) {
    const updatedPoint = {
      ...point,
      metadata: { ...point.metadata, labelText },
      updatedAt: now(),
      synced: 0 as const,
    };
    await db.floorPlanPoints.put(updatedPoint);
    await upsertPendingSyncItem('floor_plan_point', updatedPoint.id, updatedPoint);
  }
}

async function loadProjectInterventionEntries(projectId: string): Promise<InterventionEntry[]> {
  const [mappings, structures] = await Promise.all([
    db.mappingEntries.where('projectId').equals(projectId).toArray(),
    db.structureEntries.where('projectId').equals(projectId).toArray(),
  ]);

  return [
    ...mappings.map((entry): InterventionEntry => ({ kind: 'mapping', entry })),
    ...structures.map((entry): InterventionEntry => ({ kind: 'structure', entry })),
  ];
}

/**
 * Rinumera gli interventi del progetto: per ogni piano assegna 1..N a mappature e
 * strutture insieme, conservando l'ordine attuale (numero intervento, poi data).
 * Usata dopo un'eliminazione per chiudere i buchi nella sequenza.
 */
export async function resequenceProjectInterventions(projectId: string): Promise<void> {
  const all = await loadProjectInterventionEntries(projectId);
  if (all.length === 0) return;

  const floors = new Map<string, InterventionEntry[]>();
  for (const item of all) {
    const key = item.entry.floor || '';
    if (!floors.has(key)) floors.set(key, []);
    floors.get(key)!.push(item);
  }

  let hasChanges = false;

  for (const floorEntries of Array.from(floors.values())) {
    floorEntries.sort((a, b) => {
      const byIntervention = compareInterventionValues(a.entry.intervention, b.entry.intervention);
      if (byIntervention !== 0) return byIntervention;
      return a.entry.timestamp - b.entry.timestamp;
    });

    for (let index = 0; index < floorEntries.length; index += 1) {
      const item = floorEntries[index];
      const nextIntervention = String(index + 1);
      if ((item.entry.intervention || '') === nextIntervention) continue;

      if (item.kind === 'mapping') {
        const updated: MappingEntry = {
          ...item.entry,
          intervention: nextIntervention,
          lastModified: now(),
          version: item.entry.version + 1,
          synced: 0,
        };
        await db.mappingEntries.put(updated);
        await upsertPendingSyncItem('mapping_entry', updated.id, updated);
        await updateLinkedFloorPlanPoints(updated.id, buildFloorPlanLabel(updated, 'Punto'));
      } else {
        const updated: StructureEntry = {
          ...item.entry,
          intervention: nextIntervention,
          lastModified: now(),
          version: item.entry.version + 1,
          synced: 0,
        };
        await db.structureEntries.put(updated);
        await upsertPendingSyncItem('structure_entry', updated.id, updated);
        await updateLinkedFloorPlanPoints(updated.id, buildFloorPlanLabel(updated, 'Struttura'));
      }

      hasChanges = true;
    }
  }

  if (hasChanges) {
    triggerImmediateUpload();
  }
}

/**
 * Rinumera gli interventi di un piano seguendo un ordine deciso dal chiamante
 * (tipicamente i punti della planimetria da sinistra a destra).
 *
 * `orderedEntryIds` sono le voci posizionate, già ordinate; le altre voci dello
 * stesso piano prendono i numeri successivi, altrimenti resterebbero indietro con
 * numeri già riassegnati, cioè dei doppioni. Mappature e strutture sono un'unica
 * sequenza: l'id viene smistato sulla tabella giusta indipendentemente da come è
 * arrivato, perché i punti più vecchi tengono l'id struttura in `mappingEntryId`.
 *
 * Le etichette dei punti delle strutture vengono riallineate qui; per le mappature
 * l'etichetta è più ricca (tipologici, numero foto) e la ricalcola il chiamante,
 * che riceve gli id aggiornati.
 */
export async function renumberFloorInterventions(params: {
  projectId: string;
  floor: string;
  orderedEntryIds: string[];
  /** Chi ha lanciato la rinumerazione, registrato su `modifiedBy` */
  userId?: string;
}): Promise<{ mappingIds: string[]; structureIds: string[] }> {
  const all = await loadProjectInterventionEntries(params.projectId);
  const onFloor = all.filter(item => item.entry.floor === params.floor);
  const byId = new Map(onFloor.map(item => [item.entry.id, item]));

  const placed = params.orderedEntryIds
    .map(id => byId.get(id))
    .filter((item): item is InterventionEntry => Boolean(item));
  const placedIds = new Set(placed.map(item => item.entry.id));

  const rest = onFloor
    .filter(item => !placedIds.has(item.entry.id))
    .sort((a, b) => {
      const byIntervention = compareInterventionValues(a.entry.intervention, b.entry.intervention);
      return byIntervention !== 0 ? byIntervention : a.entry.timestamp - b.entry.timestamp;
    });

  const mappingIds: string[] = [];
  const structureIds: string[] = [];
  let hasChanges = false;

  const ordered = [...placed, ...rest];
  for (let index = 0; index < ordered.length; index += 1) {
    const item = ordered[index];
    const nextIntervention = String(index + 1);
    if ((item.entry.intervention || '') === nextIntervention) {
      if (item.kind === 'mapping') mappingIds.push(item.entry.id);
      continue;
    }

    if (item.kind === 'mapping') {
      const updated: MappingEntry = {
        ...item.entry,
        intervention: nextIntervention,
        lastModified: now(),
        modifiedBy: params.userId ?? item.entry.modifiedBy,
        version: item.entry.version + 1,
        synced: 0,
      };
      await db.mappingEntries.put(updated);
      await upsertPendingSyncItem('mapping_entry', updated.id, updated);
      mappingIds.push(updated.id);
    } else {
      const updated: StructureEntry = {
        ...item.entry,
        intervention: nextIntervention,
        lastModified: now(),
        modifiedBy: params.userId ?? item.entry.modifiedBy,
        version: item.entry.version + 1,
        synced: 0,
      };
      await db.structureEntries.put(updated);
      await upsertPendingSyncItem('structure_entry', updated.id, updated);
      await updateLinkedFloorPlanPoints(updated.id, buildFloorPlanLabel(updated, 'Struttura'));
      structureIds.push(updated.id);
    }

    hasChanges = true;
  }

  if (hasChanges) {
    triggerImmediateUpload();
  }

  return { mappingIds, structureIds };
}

/**
 * Prossimo numero intervento libero sul piano: il più alto già usato da mappature
 * o strutture di quel piano, più uno.
 */
export async function getNextInterventionNumber(projectId: string, floor: string): Promise<string> {
  const all = await loadProjectInterventionEntries(projectId);
  const max = all
    .filter(item => item.entry.floor === floor)
    .reduce((highest, item) => {
      const value = parseInt(item.entry.intervention || '0', 10);
      return !isNaN(value) && value > highest ? value : highest;
    }, 0);

  return String(max + 1);
}

/**
 * Dice se il numero intervento è già usato sullo stesso piano, da una mappatura o
 * da una struttura. Serve ad avvisare l'utente prima che crei un doppione: non
 * modifica nulla e non blocca il salvataggio.
 */
export async function findInterventionConflict(params: {
  projectId: string;
  floor: string;
  intervention: string;
  excludeEntryId?: string;
}): Promise<'mapping' | 'structure' | null> {
  const intervention = params.intervention.trim();
  if (!intervention) return null;

  const all = await loadProjectInterventionEntries(params.projectId);
  const conflict = all.find(item =>
    item.entry.id !== params.excludeEntryId &&
    item.entry.floor === params.floor &&
    (item.entry.intervention || '').trim() === intervention
  );

  return conflict ? conflict.kind : null;
}
